import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CollapsibleSection from '../../../../shared/components/CollapsibleSection';
import EmptyStateMessage from '../../../../shared/components/EmptyStateMessage';
import HistoryIcon from '@mui/icons-material/History';
import ExecutionCard from './ExecutionCard';
import FailedResourcesList from './FailedResourcesList';
import type { PhaseKey } from './derivePhases';
import { isGenuinelyPaused, triggerDescription } from '../healthUtils';
import { useOpsService } from '../../../../shared/api/opsService';
import type { ReconcilerExecution } from '../../../../types/frontend';
import type { ReconcilerHealth } from '../../../../types/backend';

const PHASE_KEYS: PhaseKey[] = ['observe', 'plan', 'execute', 'verify'];

function toDeclaredPhases(phases?: string[]): PhaseKey[] | undefined {
  if (!phases) return undefined;
  return phases.filter((p): p is PhaseKey => (PHASE_KEYS as string[]).includes(p));
}

export interface ReconcilerDetailPaneProps {
  name: string;
  executions: ReconcilerExecution[];
  health?: ReconcilerHealth;
  detailLoading?: boolean;
  /** Whether this reconciler has a manual-trigger RPC (gates "Run Now"). */
  canTrigger?: boolean;
  /** Decode a raw resource id into human label parts; defaults to a single raw part. */
  formatResourceParts?: (resourceId: string) => string[];
  onPause?: (domain: string) => Promise<void>;
  onResume?: (domain: string) => Promise<void>;
  onTrigger?: (domain: string) => Promise<void>;
}

function emptyStateDescription(health?: ReconcilerHealth): string {
  const schedule = health?.schedule;
  if (!schedule) return 'No executions in this window.';
  switch (schedule.trigger_kind) {
    case 'event_driven':
      return 'Runs on events — no executions in this window.';
    case 'manual':
      return 'Runs on demand.';
    case 'scheduled':
      return schedule.enabled === false
        ? 'This reconciler is paused. Resume the schedule or click Run Now.'
        : 'No executions in this window.';
    default:
      return 'No executions in this window.';
  }
}

const ReconcilerDetailPane: React.FC<ReconcilerDetailPaneProps> = ({
  name,
  executions,
  health,
  detailLoading,
  canTrigger = false,
  formatResourceParts = (id) => [id],
  onPause,
  onResume,
  onTrigger,
}) => {
  const [olderExpanded, setOlderExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deepHealth, setDeepHealth] = useState<ReconcilerHealth | null>(null);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [failuresError, setFailuresError] = useState<string | null>(null);
  const [latest, ...older] = executions;

  const ops = useOpsService();

  // The global list leaves `failures` empty; fetch the deep single-reconciler
  // lens on selection to populate the failed-resource drill-down.
  useEffect(() => {
    let cancelled = false;
    setDeepHealth(null);
    setFailuresError(null);
    setFailuresLoading(true);
    ops
      .getReconcilerHealth(name)
      .then((result) => {
        if (!cancelled) setDeepHealth(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setFailuresError(err instanceof Error ? err.message : 'Failed to load failed resources');
        }
      })
      .finally(() => {
        if (!cancelled) setFailuresLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [name, ops]);

  const convergence = deepHealth?.convergence;
  const failures = deepHealth?.failures ?? [];
  const totalFailed = convergence?.failed ?? 0;
  const staleCount = convergence?.failed_stale ?? 0;
  const activeCount = Math.max(totalFailed - staleCount, 0);
  const hasFailures = totalFailed > 0 || failures.length > 0;
  const capped = failures.length === 100 && totalFailed > 100;

  const schedule = health?.schedule;
  const paused = schedule ? isGenuinelyPaused(schedule) : false;
  const isScheduled = schedule?.trigger_kind === 'scheduled';
  const declaredPhases = toDeclaredPhases(health?.phases);

  const handleAction = async (action: string, fn?: (domain: string) => Promise<void>) => {
    if (!fn) return;
    setActionLoading(action);
    try {
      await fn(name);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box sx={{ p: 3, overflow: 'auto', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            {paused && <Chip label="Paused" size="small" color="warning" variant="outlined" />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {executions.length} run{executions.length !== 1 ? 's' : ''}
            {schedule ? ` · ${triggerDescription(schedule)}` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {isScheduled && (
            <Tooltip title={paused ? 'Resume schedule' : 'Pause schedule'}>
              <span>
                <IconButton
                  size="small"
                  color={paused ? 'warning' : 'default'}
                  disabled={actionLoading !== null}
                  onClick={() =>
                    handleAction(paused ? 'resume' : 'pause', paused ? onResume : onPause)
                  }
                >
                  {actionLoading === 'pause' || actionLoading === 'resume' ? (
                    <CircularProgress size={20} />
                  ) : paused ? (
                    <PlayCircleIcon />
                  ) : (
                    <PauseCircleIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canTrigger && onTrigger && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                actionLoading === 'trigger' ? (
                  <CircularProgress size={16} />
                ) : (
                  <PlayArrowIcon />
                )
              }
              disabled={actionLoading !== null}
              onClick={() => handleAction('trigger', onTrigger)}
            >
              Run Now
            </Button>
          )}
        </Box>
      </Box>

      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : latest ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Latest Run
          </Typography>
          <ExecutionCard execution={latest} variant="inline" declaredPhases={declaredPhases} formatResourceParts={formatResourceParts} />

          {older.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <CollapsibleSection
                title={`${older.length} older run${older.length !== 1 ? 's' : ''}`}
                expanded={olderExpanded}
                onChange={setOlderExpanded}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {older.map((exec) => (
                    <ExecutionCard key={exec.id} execution={exec} declaredPhases={declaredPhases} formatResourceParts={formatResourceParts} />
                  ))}
                </Box>
              </CollapsibleSection>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: 6 }}>
          <EmptyStateMessage
            icon={HistoryIcon}
            title={paused ? 'Reconciler paused' : 'No executions yet'}
            description={emptyStateDescription(health)}
          />
        </Box>
      )}

      {failuresError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {failuresError}
        </Alert>
      )}

      {!failuresError && hasFailures && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              Failed resources
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                {activeCount} active
              </Box>
              {' · '}
              {staleCount} stale
            </Typography>
            {failuresLoading && <CircularProgress size={12} />}
          </Box>

          <FailedResourcesList failures={failures} formatResourceParts={formatResourceParts} />

          {capped && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}
            >
              Showing 100 of {totalFailed}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReconcilerDetailPane;
